import {Component, inject} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {MatCardModule} from '@angular/material/card';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {CommonModule} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {User} from '../../../../core/classes/user';
import {passwordComplexityValidator} from '../../../../shared/validators/password-complexity.validator';
import {matchValidator} from '../../../../shared/validators/match.validator';
import {AuthService} from '../../../../services/auth.service';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {checkEmailExistsValidator} from '../../../../shared/validators/check-email-exists';
import {checkUsernameExistsValidator} from '../../../../shared/validators/check-username-exists';
import {MatStep, MatStepLabel, MatStepper, MatStepperNext, MatStepperPrevious} from '@angular/material/stepper';
import {Poc} from '../../../../core/classes/poc';
import {PhoneNumberPipe} from '../../../../shared/pipes/phone-number.pipe';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { SNACKBAR_DURATION } from '../../../../shared/constants/snackbar.constants';

@Component({
    selector: 'tgrid-account-request',
    imports: [MatCardModule, MatInputModule, MatButtonModule, CommonModule, ReactiveFormsModule, MatFormFieldModule, MatIconModule, MatSnackBarModule, MatStepper, MatStep, MatStepLabel, MatStepperNext, MatStepperPrevious],
    templateUrl: './account-request.component.html',
    styleUrl: './account-request.component.scss',
})
export class AccountRequestComponent {
  private authService = inject(AuthService);
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);

  private phoneNumberPipe = new PhoneNumberPipe();

  public userInfoGroup: FormGroup;
  public pocFormGroup: FormGroup;
  public accountInfoGroup: FormGroup;
  public pocNeeded: boolean;
  constructor() {

    this.userInfoGroup = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      organization: ['', Validators.required],
      email: ['', [Validators.required, Validators.email], [checkEmailExistsValidator(this.authService)]],
    });

    this.pocFormGroup = this.formBuilder.group({
      name: ['', Validators.required],
      organization: ['', Validators.required],
      department: ['', Validators.required],
      title: ['', Validators.required],
      email: ['', {
        updateOn: 'blur',
        validators: [Validators.required, Validators.email],
    }],
      telephone: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(14)]],
    });

    this.accountInfoGroup = this.formBuilder.group({
      username: ['',[ Validators.required], [checkUsernameExistsValidator(this.authService)]],
      reason: ['', Validators.required],
      password: ['', [Validators.required], [passwordComplexityValidator(this.authService)]],
      confirmPassword: ['', [Validators.required]],
    }, {
      validators: matchValidator('password', 'confirmPassword'),
    });


    this.pocFormGroup.controls['telephone'].valueChanges.pipe(
      takeUntilDestroyed(),
    ).subscribe((value: string) => {
      const numbers = value.match(/\d+/g);
      if(numbers && numbers.length > 0){
        this.pocFormGroup.controls['telephone'].setValue(this.phoneNumberPipe.transform(numbers.join('')), {
          emitEvent: false,
          onlySelf: true,
        });
      }
    });
  }

  onSubmit() {
    const accountInfo = this.accountInfoGroup.getRawValue();

    const user = new User(this.userInfoGroup.getRawValue());
    user.username = accountInfo.username;
    user.password = accountInfo.password;
    user.reason = accountInfo.reason;

    if (this.pocNeeded) {
      user.pointOfContact = new Poc(this.pocFormGroup.getRawValue());
      const onlyNumbers = user.pointOfContact.telephone.match(/\d+/g);
      if(onlyNumbers !== null){
        user.pointOfContact.telephone = onlyNumbers.join('');
      }
    }

    this.authService.requestAccount(user).subscribe(() => {
        this.snackBar.open('Account request submitted!', 'X', { duration: SNACKBAR_DURATION.NORMAL })
          .afterDismissed()
          .subscribe(() =>  this.router.navigate(['/login']));
    });
  }

  public checkPocNeeded(): void {
    const email = this.userInfoGroup.get('email')?.value;
    if (email) {
      this.pocNeeded = !User.nonPocEmailExtension.some((ext) => email.endsWith(ext));
    }
  }

}
